/**
 * Author: Romain Hapi Sr.
 * Version: 2.0
 * Date: 2026-09-10
 * Project: Prenim Digital
 * Copyright: Prenim UK
 */
/**
 * Page « Carrieres » du site Tchad, avec le formulaire de candidature.
 *
 * Les offres viennent de l'API et retombent sur `mockCareers` si elle ne repond
 * pas : mieux vaut afficher les postes connus qu'une page vide.
 *
 * `META` et `EYEBROW` portent les libelles d'interface en local, comme sur les
 * autres pages du Tchad, ou les traductions sont dans le bundle.
 */
import React, { useState, useEffect } from 'react';
import { MapPin, Clock, Send, Paperclip, Check } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { PageHero, SectionHead } from '../components/Section';
import { useReveal } from '../hooks/useReveal';
import SEO from '../../components/SEO';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from '../../components/ui/sonner';
import { getCareers, submitCareerApplication } from '../services/api';
import { mockCareers } from '../mockData';
import { SHELL, SECTION, CARD, BTN_PRIMARY } from '../theme';

const META = {
  fr: {
    title: 'Carrieres — Prenim Tchad',
    description: "Postes ouverts chez Prenim Tchad, a N'Djamena et a Leeds. Rejoignez l'equipe.",
  },
  en: {
    title: 'Careers — Prenim Tchad',
    description: "Open roles at Prenim Tchad, in N'Djamena and Leeds. Join the team.",
  },
  ar: { title: 'الوظائف — Prenim Tchad', description: 'الوظائف الشاغرة في نجامينا وليدز.' },
};

const EYEBROW = { fr: 'Nous rejoindre', en: 'Join us', ar: 'انضم إلينا' };
const COPY = {
  fr: {
    benefits: 'Ce que nous offrons',
    openings: 'Postes ouverts',
    loading: 'Chargement des offres…',
    empty: "Aucune offre ouverte pour le moment. Ecrivez-nous : les candidatures spontanees sont les bienvenues.",
    formIntro:
      'Remplissez le formulaire ci-dessous pour postuler. Les champs marques d’une etoile sont obligatoires.',
    cover: 'Lettre de motivation',
    cv: 'CV',
    cvHint: '(PDF, DOC, DOCX — max 5 Mo)',
    choose: 'Choisir un fichier…',
    submit: 'Envoyer ma candidature',
    sending: 'Envoi en cours…',
  },
  en: {
    benefits: 'What we offer',
    openings: 'Open roles',
    loading: 'Loading roles…',
    empty: 'No open role right now. Write to us — speculative applications are welcome.',
    formIntro: 'Fill in the form below to apply. Fields marked with a star are required.',
    cover: 'Cover letter',
    cv: 'CV',
    cvHint: '(PDF, DOC, DOCX — max 5 MB)',
    choose: 'Choose a file…',
    submit: 'Send my application',
    sending: 'Sending…',
  },
  ar: {
    benefits: 'ما نقدمه',
    openings: 'الوظائف الشاغرة',
    loading: 'جارٍ التحميل…',
    empty: 'لا توجد وظائف شاغرة حالياً. راسلونا، الطلبات التلقائية مرحب بها.',
    formIntro: 'املأ النموذج أدناه للتقديم. الحقول المعلمة بنجمة إلزامية.',
    cover: 'رسالة تحفيزية',
    cv: 'السيرة الذاتية',
    cvHint: '(PDF, DOC, DOCX — 5 ميغابايت كحد أقصى)',
    choose: 'اختر ملفاً…',
    submit: 'إرسال طلبي',
    sending: 'جارٍ الإرسال…',
  },
};

const BENEFITS = [
  { fr: 'Salaire compétitif', en: 'Competitive salary', ar: 'راتب تنافسي' },
  { fr: 'Télétravail flexible', en: 'Flexible remote work', ar: 'عمل عن بعد مرن' },
  { fr: 'Formation continue', en: 'Continuous training', ar: 'التدريب المستمر' },
  { fr: 'Projets innovants', en: 'Innovative projects', ar: 'مشاريع مبتكرة' },
  { fr: 'Équipe internationale', en: 'International team', ar: 'فريق دولي' },
  { fr: 'Évolution de carrière', en: 'Career growth', ar: 'التطور المهني' },
];

// Le backend refuse au-dela de 5 Mo. Le verifier ici evite un televersement
// inutile et une erreur incomprehensible apres une longue attente.
const MAX_CV_BYTES = 5 * 1024 * 1024;

export const Careers = () => {
  const { t, language } = useLanguage();
  const [selectedJob, setSelectedJob] = useState(null);
  const [careers, setCareers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    coverLetter: '',
  });
  const [cvFile, setCvFile] = useState(null);

  const benefitsReveal = useReveal();
  const jobsReveal = useReveal();

  useEffect(() => {
    let alive = true;
    const fetchCareers = async () => {
      try {
        const data = await getCareers();
        if (!alive) return;
        setCareers(Array.isArray(data) && data.length ? data : mockCareers);
      } catch (error) {
        console.error('Error fetching careers:', error);
        if (alive) setCareers(mockCareers);
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetchCareers();
    return () => {
      alive = false;
    };
  }, []);

  const copy = COPY[language] || COPY.fr;
  const meta = META[language] || META.fr;

  const handleApply = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const response = await submitCareerApplication({
        jobId: selectedJob.id,
        ...formData,
        cv: cvFile,
      });
      toast.success(
        response.message ||
          'Candidature envoyée! Nous étudierons votre profil et vous contacterons bientôt.'
      );
      setFormData({ firstName: '', lastName: '', email: '', phone: '', coverLetter: '' });
      setCvFile(null);
      setSelectedJob(null);
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setSending(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0] || null;
    if (file && file.size > MAX_CV_BYTES) {
      toast.error('Ce fichier dépasse 5 Mo. Merci d’en choisir un plus léger.');
      e.target.value = '';
      setCvFile(null);
      return;
    }
    setCvFile(file);
  };

  const labelClass = 'block text-sm font-semibold text-slate-700';

  return (
    <main data-testid="tchad-careers-page">
      <SEO title={meta.title} description={meta.description} lang={language} />

      <PageHero
        eyebrow={EYEBROW[language] || EYEBROW.fr}
        title={t.careers.title}
        lead={t.careers.subtitle}
      />

      <section className={`${SECTION} bg-white`}>
        <div className={SHELL}>
          <SectionHead title={copy.benefits} />
          <div
            ref={benefitsReveal.ref}
            className={`${benefitsReveal.className} mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3`}
          >
            {BENEFITS.map((benefit) => (
              <div
                key={benefit.fr}
                className="flex items-center gap-3 rounded-lg border border-slate-200 px-5 py-4"
              >
                <Check className="h-4 w-4 shrink-0 text-amber-600" />
                <span className="no-justify text-sm font-medium text-slate-700">
                  {benefit[language] || benefit.fr}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className={`${SECTION} bg-slate-50`}>
        <div className={SHELL}>
          <SectionHead title={copy.openings} />

          {loading ? (
            <p className="no-justify mt-12 text-slate-600">{copy.loading}</p>
          ) : careers.length === 0 ? (
            <p className="no-justify mt-12 text-slate-600">{copy.empty}</p>
          ) : (
            <div
              ref={jobsReveal.ref}
              className={`${jobsReveal.className} mt-12 grid gap-6 md:grid-cols-2`}
            >
              {careers.map((job) => (
                <div
                  key={job.id}
                  data-testid={`tchad-job-${job.id}`}
                  className={`${CARD} flex flex-col p-8`}
                >
                  <h3 className="text-xl font-bold tracking-tight text-slate-950 lg:text-2xl">
                    {job.title[language]}
                  </h3>

                  <div className="no-justify mt-3 flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-600">
                    <span className="inline-flex items-center gap-1.5">
                      <MapPin className="h-4 w-4 text-slate-400" />
                      {job.location[language]}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Clock className="h-4 w-4 text-slate-400" />
                      {job.type[language]}
                    </span>
                  </div>

                  <p className="mt-5 flex-1 text-sm leading-relaxed text-slate-600">
                    {job.description[language]}
                  </p>

                  <Dialog
                    onOpenChange={(open) => {
                      if (!open) setSelectedJob(null);
                    }}
                  >
                    <DialogTrigger asChild>
                      <button
                        type="button"
                        onClick={() => setSelectedJob(job)}
                        data-testid={`tchad-apply-${job.id}`}
                        className={`${BTN_PRIMARY} mt-8 w-full justify-center`}
                      >
                        {t.careers.apply}
                      </button>
                    </DialogTrigger>

                    <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[520px]">
                      <DialogHeader>
                        <DialogTitle className="text-2xl tracking-tight">
                          {job.title[language]}
                        </DialogTitle>
                        <DialogDescription className="no-justify">
                          {copy.formIntro}
                        </DialogDescription>
                      </DialogHeader>

                      <form onSubmit={handleApply} className="mt-4 space-y-4">
                        <div className="grid gap-3 sm:grid-cols-2">
                          <div>
                            <label htmlFor={`apply-first-${job.id}`} className={labelClass}>
                              {t.contact.first_name} *
                            </label>
                            <Input
                              id={`apply-first-${job.id}`}
                              className="mt-2"
                              type="text"
                              name="firstName"
                              autoComplete="given-name"
                              value={formData.firstName}
                              onChange={handleChange}
                              required
                            />
                          </div>
                          <div>
                            <label htmlFor={`apply-last-${job.id}`} className={labelClass}>
                              {t.contact.last_name} *
                            </label>
                            <Input
                              id={`apply-last-${job.id}`}
                              className="mt-2"
                              type="text"
                              name="lastName"
                              autoComplete="family-name"
                              value={formData.lastName}
                              onChange={handleChange}
                              required
                            />
                          </div>
                        </div>

                        <div>
                          <label htmlFor={`apply-email-${job.id}`} className={labelClass}>
                            {t.contact.email} *
                          </label>
                          <Input
                            id={`apply-email-${job.id}`}
                            className="mt-2"
                            type="email"
                            name="email"
                            autoComplete="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                          />
                        </div>

                        <div>
                          <label htmlFor={`apply-phone-${job.id}`} className={labelClass}>
                            {t.contact.phone}
                          </label>
                          <Input
                            id={`apply-phone-${job.id}`}
                            className="mt-2"
                            type="tel"
                            name="phone"
                            autoComplete="tel"
                            value={formData.phone}
                            onChange={handleChange}
                          />
                        </div>

                        <div>
                          <label htmlFor={`apply-cover-${job.id}`} className={labelClass}>
                            {copy.cover} *
                          </label>
                          <Textarea
                            id={`apply-cover-${job.id}`}
                            className="mt-2"
                            name="coverLetter"
                            value={formData.coverLetter}
                            onChange={handleChange}
                            required
                            rows={5}
                          />
                        </div>

                        <div>
                          <span className={labelClass}>
                            {copy.cv}{' '}
                            <span className="font-normal text-slate-400">{copy.cvHint}</span>
                          </span>
                          <label
                            htmlFor={`apply-cv-${job.id}`}
                            className="mt-2 flex cursor-pointer items-center gap-3 rounded-lg border-2 border-dashed border-slate-300 px-4 py-3 transition-colors hover:border-amber-500 hover:bg-amber-50"
                          >
                            <Paperclip className="h-5 w-5 shrink-0 text-amber-600" />
                            <span className="no-justify truncate text-sm text-slate-600">
                              {cvFile ? cvFile.name : copy.choose}
                            </span>
                            <input
                              id={`apply-cv-${job.id}`}
                              type="file"
                              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                              onChange={handleFileChange}
                              className="sr-only"
                            />
                          </label>
                        </div>

                        <button
                          type="submit"
                          disabled={sending}
                          className={`${BTN_PRIMARY} w-full justify-center disabled:cursor-not-allowed disabled:opacity-60`}
                        >
                          <Send className="h-4 w-4 rtl-flip" />
                          {sending ? copy.sending : copy.submit}
                        </button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
};
